"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDatasheets = exports.useDatasheet = void 0;
const react_1 = require("react");
const context_1 = require("../context");
const use_meta_1 = require("./use_meta");
const model_1 = require("../model");
const store_1 = require("../store");
const message_1 = require("../message");
const private_1 = require("../utils/private");
/**
 * A hook for connecting a React component to your datasheet's schema.
 * Datasheet will provide the interface to make update to datasheet date, and check permission.
 *
 * @param
 *
 * @returns Datasheet instance
 * ### Example
 * ```js
 * import { useDatasheet } from '@apitable/widget-sdk';
 *
 * function AddRecord() {
 *   const datasheet = useDatasheet();
 *   const [error, setError] = useState();
 *   // The key of the parameter is the fieldId and the value is the cell value
 *   const valuesMap = {
 *     fld1234567980: 'this is a text value',
 *     fld0987654321: 1024,
 *   }
 *   function addRecord(valuesMap) {
 *     if (!datasheet) {
 *       return;
 *     }
 *     const permission = datasheet.checkPermissionsForAddRecord(valuesMap)
 *     if (permission.acceptable) {
 *       datasheet.addRecord(valuesMap);
 *       return;
 *     }
 *     setError(permission.message);
 *   }
 *   return (<div>
 *     {error && <p>{error}</p>}
 *     <button onClick={() => addRecord(valuesMap)}>add a new record</button>
 *   </div>);
 * }
 *
 * ```
 *
 */
function useDatasheet(datasheetId) {
    var _a;
    const context = (0, react_1.useContext)(context_1.WidgetContext);
    const { datasheetId: metaDatasheetId } = (0, use_meta_1.useMeta)();
    const _datasheetId = datasheetId || metaDatasheetId;
    const datasheetObj = (0, store_1.getWidgetDatasheetPack)(context.widgetStore.getState(), datasheetId);
    if (datasheetId && (!datasheetObj || ((_a = datasheetObj.datasheet) === null || _a === void 0 ? void 0 : _a.isPartOfData))) {
        (0, private_1.isSandbox)() ? message_1.widgetMessage.fetchDatasheet({ datasheetId }) : message_1.eventMessage.fetchDatasheet({ datasheetId }, context.id);
    }
    return (0, react_1.useMemo)(() => {
        if (!_datasheetId)
            return undefined;
        return new model_1.Datasheet(_datasheetId, context);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_datasheetId, context, datasheetObj]);
}
exports.useDatasheet = useDatasheet;
function useDatasheets(datasheetIds) {
    var _a;
    const context = (0, react_1.useContext)(context_1.WidgetContext);
    for (const datasheetId of datasheetIds) {
        const datasheetObj = (0, store_1.getWidgetDatasheetPack)(context.widgetStore.getState(), datasheetId);
        if (datasheetId && (!datasheetObj || ((_a = datasheetObj.datasheet) === null || _a === void 0 ? void 0 : _a.isPartOfData))) {
            (0, private_1.isSandbox)() ? message_1.widgetMessage.fetchDatasheet({ datasheetId }) : message_1.eventMessage.fetchDatasheet({ datasheetId }, context.id);
        }
    }
    return (0, react_1.useMemo)(() => {
        if (!datasheetIds)
            return undefined;
        const datasheets = [];
        for (const datasheetId of datasheetIds) {
            datasheets.push(new model_1.Datasheet(datasheetId, context));
        }
        return datasheets;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasheetIds, context]);
}
exports.useDatasheets = useDatasheets;
